import React from 'react';
import { Star, Camera, MessageSquareQuote, Share2, UserRound, Mail, AlertTriangle } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { BACKEND_URL, getToken } from '../../../lib/easerApi';

export const fmtAvg = (v) => (v === null || v === undefined ? '—' : Number(v).toFixed(1).replace('.', ','));
export const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
export const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
export const qcPhotoUrl = (controlId, index) => `${BACKEND_URL}/api/easer/qualite/${controlId}/photos/${index}?token=${encodeURIComponent(getToken())}`;

/** Étoiles 1..5 : lecture seule (value) ou saisie (onChange). Cibles tactiles 44 px en mode saisie. */
export function StarRating({ value = 0, onChange = null, size = 'h-4 w-4', testId, label }) {
  const interactive = typeof onChange === 'function';
  return (
    <span className={`inline-flex items-center ${interactive ? 'gap-0' : 'gap-0.5'}`} role={interactive ? 'radiogroup' : undefined} aria-label={label || `${value} sur 5`} data-testid={testId}>
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= Math.round(value || 0);
        const cls = `${size} transition-transform ${filled ? 'fill-[hsl(var(--brand-apricot))] text-[hsl(var(--brand-apricot))]' : 'text-muted-foreground/40'}`;
        if (!interactive) return <Star key={n} className={cls} />;
        return (
          <button key={n} type="button" role="radio" aria-checked={value === n} aria-label={`${n} étoile${n > 1 ? 's' : ''}`} onClick={() => onChange(n)} data-testid={testId ? `${testId}-${n}` : undefined}
            className="h-11 w-11 flex items-center justify-center rounded-md hover:bg-secondary active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]">
            <Star className={cls} />
          </button>
        );
      })}
    </span>
  );
}

export const EMAIL_STATUS = {
  sent: { label: 'E-mail envoyé au client', cls: 'bg-[hsl(var(--brand-mint))] text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30', icon: Mail },
  failed: { label: "E-mail non parti", cls: 'bg-destructive/10 text-destructive border-destructive/30', icon: AlertTriangle },
  skipped: { label: 'Pas d\'e-mail (adresse absente)', cls: 'bg-secondary text-muted-foreground border-border', icon: Mail },
};

/** Corps d'un contrôle qualité : moyenne, étoiles par critère, compte rendu, photos, réponse de l'Easer. */
export function QcBody({ control, photoUrl = qcPhotoUrl, showReply = true, compact = false, showShare = true }) {
  const c = control;
  const email = EMAIL_STATUS[c.email_status];
  return (
    <div className={`space-y-3 ${compact ? 'text-sm' : ''}`} data-testid={`qc-body-${c.id}`}>
      <div className="flex items-center gap-3">
        <span className="h-12 w-12 rounded-xl bg-[hsl(var(--brand-apricot))]/15 text-[hsl(var(--brand-apricot))] flex items-center justify-center flex-shrink-0"><Star className="h-5 w-5 fill-current" /></span>
        <div className="min-w-0 flex-1">
          <p className="text-2xl font-semibold leading-none tracking-tight" data-testid={`qc-average-${c.id}`}>{fmtAvg(c.average)}<span className="text-sm text-muted-foreground font-normal"> / 5</span></p>
          <p className="text-xs text-muted-foreground mt-1 flex flex-wrap items-center gap-x-2">
            <span className="inline-flex items-center gap-1"><UserRound className="h-3 w-3" /> {c.manager_name || 'Manager'}</span>
            <span>· {fmtDateTime(c.created_at)}</span>
          </p>
        </div>
        {showShare && (c.share_with_client
          ? <Badge className="border text-[10px] bg-[hsl(var(--brand-sky))]/40 text-[hsl(var(--brand-ink))] border-[hsl(var(--brand-sky))]" data-testid={`qc-shared-${c.id}`}><Share2 className="h-3 w-3 mr-1" /> Visible par le client</Badge>
          : <Badge variant="outline" className="text-[10px] text-muted-foreground" data-testid={`qc-internal-${c.id}`}>Interne</Badge>)}
      </div>
      <ul className="divide-y divide-border rounded-xl border border-border" data-testid={`qc-criteria-${c.id}`}>
        {(c.criteria || []).map((cr) => (
          <li key={cr.id} className="flex items-center justify-between gap-2 px-3 py-1.5">
            <span className="text-sm">{cr.label}</span>
            <StarRating value={cr.stars} size="h-4 w-4" testId={`qc-criterion-${c.id}-${cr.id}`} />
          </li>
        ))}
      </ul>
      {c.report && <p className="text-sm whitespace-pre-line rounded-xl bg-secondary/60 px-3 py-2" data-testid={`qc-report-${c.id}`}>{c.report}</p>}
      {c.photos?.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid={`qc-photos-${c.id}`}>
          {c.photos.map((p) => (
            <a key={p.index} href={photoUrl(c.id, p.index)} target="_blank" rel="noopener noreferrer" className="relative flex-shrink-0" aria-label={p.caption || `Photo ${p.index + 1}`}>
              {p.thumb ? <img src={p.thumb} alt={p.caption || `Photo ${p.index + 1}`} className="h-20 w-20 object-cover rounded-lg border border-border" /> : <span className="h-20 w-20 rounded-lg border border-border bg-secondary flex items-center justify-center"><Camera className="h-5 w-5 text-muted-foreground" /></span>}
              {p.caption && <span className="absolute bottom-1 left-1 right-1 truncate rounded bg-black/60 text-white text-[10px] px-1">{p.caption}</span>}
            </a>
          ))}
        </div>
      )}
      {showShare && c.share_with_client && email && (
        <p className="text-xs flex items-center gap-1.5" data-testid={`qc-email-status-${c.id}`}><Badge className={`border text-[10px] ${email.cls}`}><email.icon className="h-3 w-3 mr-1" /> {email.label}</Badge>{c.email_status === 'failed' && c.email_error && <span className="text-muted-foreground truncate">{c.email_error}</span>}</p>
      )}
      {showReply && c.reply && (
        <div className="rounded-xl border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 px-3 py-2 text-sm" data-testid={`qc-reply-${c.id}`}>
          <p className="text-xs text-muted-foreground flex items-center gap-1"><MessageSquareQuote className="h-3 w-3" /> Réponse de {c.reply.user_name || "l'Easer"} · {fmtDateTime(c.reply.at)}</p>
          <p className="mt-0.5 whitespace-pre-line">{c.reply.text}</p>
        </div>
      )}
    </div>
  );
}
